const EVENT_NAME_CART_ERROR = 'cart_error';
const EVENT_NAME_ATC_TIMING = 'add_to_cart_timing';
const CART_ADD_STALL_MS = 15000;

const pushCartMonitoringEvent = (payload) => {
  try {
    if (typeof window.dataLayer === 'undefined') {
      console.error(`${payload.event} - dataLayer not available.`);
      return;
    }

    window.dataLayer.push(payload);

    if (payload.event === EVENT_NAME_CART_ERROR) {
      console.info(`✓ GTM event ${EVENT_NAME_CART_ERROR} pushed to dataLayer.`);
    }
  } catch (error) {
    console.error(`Error tracking ${payload.event}:`, error);
  }
};

// True while a product-form submission is running its fetches; used by the
// fetch observer below to skip requests already tracked via theme events.
let themeAtcInFlight = false;

const trackCartError = (event) => {
  themeAtcInFlight = false;
  pushCartMonitoringEvent({
    event: EVENT_NAME_CART_ERROR,
    error_type: event.detail?.errorType || 'unknown',
    error_message: event.detail?.errorMessage || event.detail?.error || 'unknown',
    duration_ms: event.detail?.durationMs,
    variant_id: event.detail?.variantId != null ? String(event.detail.variantId) : undefined,
    source: 'theme',
    page_path: window.location.pathname
  });
};

const trackAddToCartTiming = (event) => {
  themeAtcInFlight = false;

  // Only add-to-cart submissions carry a duration; cart quantity
  // changes fire on:line-item:change without one and are skipped.
  if (typeof event.detail?.durationMs !== 'number') return;

  pushCartMonitoringEvent({
    event: EVENT_NAME_ATC_TIMING,
    duration_ms: event.detail.durationMs,
    variant_id: event.detail?.variantId != null ? String(event.detail.variantId) : undefined,
    source: 'theme',
    page_path: window.location.pathname
  });
};

/**
 * Observes every fetch() POST to /cart/add so additions made outside the
 * theme's product-form (Boost collection/search cards, comparison page) are
 * measured too. Observe-only: requests and responses are never altered.
 * A request with no response after CART_ADD_STALL_MS is reported as
 * "stalled" — the "spinner never stops" case.
 */
const initCartAddFetchObserver = () => {
  const originalFetch = window.fetch;
  if (typeof originalFetch !== 'function' || originalFetch.mxCartMonitoring) return;

  const observedFetch = function (...args) {
    let tracked = null;
    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const method = (args[1]?.method || args[0]?.method || 'GET').toUpperCase();
      if (method === 'POST' && /\/cart\/add/.test(url) && !themeAtcInFlight) {
        tracked = {
          source: document.querySelector('.boost-sd__product-item') ? 'boost' : 'other',
          startedAt: performance.now()
        };
      }
    } catch (error) {
      tracked = null;
    }

    const result = originalFetch.apply(this, args);
    if (!tracked) return result;

    // Lets page scripts recover UI stuck in a loading state (e.g. Boost's
    // add-to-cart button and the cart drawer spinner) — see mx-boost.js.
    const dispatchCartAddError = (errorType) => {
      document.dispatchEvent(new CustomEvent('on:mx:cart-add:error', {
        bubbles: true,
        detail: { source: tracked.source, errorType }
      }));
    };

    const stallTimer = setTimeout(() => {
      pushCartMonitoringEvent({
        event: EVENT_NAME_CART_ERROR,
        error_type: 'stalled',
        error_message: `no response after ${CART_ADD_STALL_MS}ms`,
        duration_ms: CART_ADD_STALL_MS,
        source: tracked.source,
        page_path: window.location.pathname
      });
      dispatchCartAddError('stalled');
    }, CART_ADD_STALL_MS);

    result.then((response) => {
      clearTimeout(stallTimer);
      const durationMs = Math.round(performance.now() - tracked.startedAt);
      if (response.ok) {
        pushCartMonitoringEvent({
          event: EVENT_NAME_ATC_TIMING,
          duration_ms: durationMs,
          source: tracked.source,
          page_path: window.location.pathname
        });
      } else {
        pushCartMonitoringEvent({
          event: EVENT_NAME_CART_ERROR,
          error_type: 'http',
          error_message: String(response.status),
          duration_ms: durationMs,
          source: tracked.source,
          page_path: window.location.pathname
        });
        dispatchCartAddError('http');
      }
    }, (error) => {
      clearTimeout(stallTimer);
      pushCartMonitoringEvent({
        event: EVENT_NAME_CART_ERROR,
        error_type: 'network',
        error_message: error?.message || String(error),
        duration_ms: Math.round(performance.now() - tracked.startedAt),
        source: tracked.source,
        page_path: window.location.pathname
      });
      dispatchCartAddError('network');
    });

    return result;
  };

  observedFetch.mxCartMonitoring = true;
  window.fetch = observedFetch;
};

window.onGtmReady(function () {
  document.addEventListener('submit', (event) => {
    if (event.target?.classList?.contains('js-product-form')) themeAtcInFlight = true;
  }, true);

  document.addEventListener('on:cart:error', trackCartError);
  document.addEventListener('on:cart:add', trackAddToCartTiming);
  document.addEventListener('on:line-item:change', trackAddToCartTiming);

  initCartAddFetchObserver();
});
